import { Component, Input } from '@angular/core';
import { IIncidents } from 'src/app/models/incident';

@Component({
  selector: 'app-recent-dashboard-details-section',
  templateUrl: './recent-dashboard-details-section.component.html',
  styleUrl: './recent-dashboard-details-section.component.scss',
})
export class RecentDashboardDetailsSectionComponent {
  @Input() recentIncidents: IIncidents[] = [];

  trackByIncidentId(index: number, incident: any): string {
    return incident.id;
  }
  statusMap: any = {
    0: 'Open',
    1: 'Close',
    2: 'Verified',
    3: 'Snoozed',
    4: 'In-Progress',
    5: 'On-Hold',
    6: 'Approved',
    7: 'Rejected',
    8: 'Completed',
  };
  statusTextMap: { [key: number]: string } = {
    0: 'Open',
    1: 'Closed', // Changed from 'Close' for better wording
    2: 'Verified',
    3: 'Snoozed',
    4: 'In-Progress',
    5: 'On-Hold',
    6: 'Approved',
    7: 'Rejected',
    8: 'Completed',
  };
  statusClassMap: { [key: number]: string } = {
    0: 'status-critical', // Open incidents are often critical
    1: 'status-resolved', // Closed incidents are resolved
    2: 'status-resolved', // Verified could also be resolved
    3: 'status-default', // Snoozed
    4: 'status-ongoing', // In-Progress
    5: 'status-default', // On-Hold
    6: 'status-resolved', // Approved
    7: 'status-critical', // Rejected
    8: 'status-resolved', // Completed
  };
  getActiveIncidentsCount(): number {
    const activeStatuses = ['Open', 'In-Progress', 'Pending']; // Adjust as needed

    return this.recentIncidents.filter((incident) => {
      const statusString = this.statusMap[incident.incidentStatus];
      return activeStatuses.includes(statusString);
    }).length;
  }
  getIncidentIcon(severity: string): string {
    const icons: any = {
      critical: '🚨',
      high: '⚠️',
      medium: '🔶',
      low: 'ℹ️',
      default: '📋',
    };
    return icons[severity?.toLowerCase()] || icons.default;
  }

  // Get time ago string
  getTimeAgo(lastUpdated: Date | string): string {
    if (!lastUpdated) return 'Unknown';

    const now = new Date();
    const updated = new Date(lastUpdated);
    const diffInMs = now.getTime() - updated.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInDays < 7) return `${diffInDays} days ago`;

    return updated.toLocaleDateString();
  }

  // Format date
  formatDate(date: Date | string): string {
    if (!date) return 'N/A';

    const d = new Date(date);
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  // Get status CSS class
  getStatusClass(status: number): string {
    const label = this.statusMap[status];
    if (!label) return 'status-default';

    // Convert to kebab-case: lowercase and replace space/hyphen with single dash
    const statusKey = label
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
    return `status-${statusKey}`;
  }

  // Get severity CSS class
  getSeverityClass(severity: string): string {
    if (!severity) return 'status-default';

    const severityLower = severity.toLowerCase();
    return `severity-${severityLower}`;
  }

  // Truncate text
  truncateText(text: string, maxLength: number): string {
    if (!text) return '';
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;
  }

  // Action methods
  viewDetails(incident: any): void {
    console.log('View details for incident:', incident.id);
    // Implement your view details logic
  }

  goToDetails(incident: any): void {
    console.log('Go to details for incident:', incident.id);
    // Implement your navigation logic
    // this.router.navigate(['/incidents', incident.id]);
  }
  getStatusInfo(statusInteger: number): {
    statusText: string;
    statusClass: string;
  } {
    const text = this.statusTextMap[statusInteger] || 'Unknown';
    let className = this.statusClassMap[statusInteger] || 'status-default';

    // Special handling for "Open" or "In-Progress" to ensure they use correct specific classes
    if (statusInteger === 0) {
      // Open
      className = 'status-critical';
    } else if (statusInteger === 4) {
      // In-Progress
      className = 'status-ongoing';
    }

    return {
      statusText: text,
      statusClass: className,
    };
  }
}
